/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * dailyAttachApi — attaching many runs per entrant on a multi-day event.
 *
 * THE SHAPE. A race keeps one event_result per registration, and the database
 * enforces it. On a multi-day event that row stays as the entrant's ENTRY
 * (status IN_PROGRESS, no time of its own), and every run the organizer
 * attaches becomes one event_result_split row under it, labelled "DD|<run>".
 * Any number per day. A run counts once: the hook refuses it a second time.
 *
 * NUMBERING. split_number is unique per entry, so two organizers attaching at
 * once collide instead of both succeeding. The next number comes from the
 * attach query's max_split_number — the organizer's row-level security does
 * not let them read event_result_split directly — and a collision is retried
 * once with a fresh number.
 *
 * NOTHING HERE IS A PERMISSION CHECK. The saved query's caller filter and the
 * validation hook decide; this module asks and reports what they said.
 */

import envConfig from '../../../config/envConfig';
import { ATTACH_BATCH, rowsAsObjects } from './attachApi';
import { encodeAttachLabel } from './dailyMode';
import { RESULT_TABLE, readError, resultPayloadFor } from './resultsApi';

export const DAILY_ATTACHABLE_QUERY = 'organizer_daily_attachable_runs';
export const SPLIT_TABLE = 'event_result_split';

/** analytics_runner answers at most this many rows (ROW_CAP). */
export const DAILY_PAGE_ROWS = 500;

/** Bounds the paging loop if the server ever stops advancing. */
const MAX_PAGES = 200;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

/** The event_result_split row for one attached run. */
export function splitPayloadFor({ resultId, splitNumber, dayNumber, run }) {
  const seconds = Math.max(0, Math.round(Number(run?.duration_seconds) || 0));
  const distance = Number(run?.distance_meters);
  const pace = Number(run?.average_pace_seconds_per_km);
  return {
    event_result_id: resultId,
    split_number: splitNumber,
    label: encodeAttachLabel(dayNumber, run.id),
    split_seconds: seconds,
    // NOT NULL in the table. Not a running total in daily mode — one written
    // at attach time would be wrong as soon as two organizers attach at once.
    cumulative_seconds: seconds,
    // NOT NULL too; yoga has no distance.
    distance_meters: Number.isFinite(distance) && distance >= 0 ? distance : 0,
    pace_seconds_per_km: Number.isFinite(pace) && pace > 0 ? Math.round(pace) : null,
  };
}

/** Waits after a 429 before asking again; the throttle is 5/sec and 60/min. */
export const THROTTLE_WAITS_MS = [1500, 3000, 6000, 12000, 20000, 30000];

const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

async function runQueryPage(api, params, signal, { strict = false } = {}) {
  const query = new URLSearchParams(params).toString();
  for (let attempt = 0; ; attempt += 1) {
    const resp = await api.apiCall(
      `/arcee/application/${Number(envConfig.applicationId)}/analytics/${DAILY_ATTACHABLE_QUERY}/run/?${query}`,
      { method: 'GET', signal },
    );
    if (resp.ok) return rowsAsObjects(await resp.json());
    // A refusal is "not your race": draw nothing, as the single-run panel does.
    if (!strict) return null;
    // STRICT is for reads that feed a write: a short list there would attach
    // fewer runs than the organizer was shown, silently. Wait out the
    // throttle; anything else is an error to report.
    if (resp.status === 429 && attempt < THROTTLE_WAITS_MS.length) {
      await pause(THROTTLE_WAITS_MS[attempt]);
      continue; // eslint-disable-line no-continue
    }
    throw new Error(resp.status === 429
      ? 'The server is busy. Wait a minute and attach again.'
      : await readError(resp, 'Could not read the runs for this day.'));
  }
}

/**
 * Everyone on this race with runs on `dayNumber` that are not attached yet,
 * each with ALL of those runs (several per day is normal).
 *
 * Paged by registrant. A full page may stop in the middle of somebody's runs,
 * so its last registrant is dropped and the next page starts from them.
 *
 * `strict` throws (after waiting out the throttle) instead of answering a
 * short list; `onProgress(runsSoFar)` is told after every page.
 */
export async function fetchDailyAttachables(api, {
  eventActivityId, dayNumber, minDistanceMeters = 0, registrationIds, strict = false, onProgress, signal,
} = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return [];
  const people = new Map();
  // Asked about a few registrants only (entriesFor, mid-attach): start just
  // before the lowest and stop past the highest, rather than reading the whole
  // day — on a big day that is dozens of reads against the analytics throttle.
  const wanted = (registrationIds || []).map(Number).filter(Number.isFinite);
  const lowest = wanted.length ? Math.min(...wanted) : null;
  const highest = wanted.length ? Math.max(...wanted) : null;
  let after = lowest !== null ? lowest - 1 : 0;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const rows = await runQueryPage(api, {
      event_activity_id: eventActivityId,
      min_distance_meters: Number(minDistanceMeters) || 0,
      day_number: dayNumber,
      after_registration_id: after,
    }, signal, { strict });
    if (!rows || !rows.length) break;

    const full = rows.length >= DAILY_PAGE_ROWS;
    let keep = rows;
    if (full) {
      const lastId = rows[rows.length - 1].registration_id;
      const trimmed = rows.filter((r) => r.registration_id !== lastId);
      // One registrant filling a whole page: keep them, or we never advance.
      if (trimmed.length) keep = trimmed;
    }

    keep.forEach((r) => {
      const key = String(r.registration_id);
      if (!people.has(key)) {
        people.set(key, {
          registrationId: r.registration_id,
          athleteUserId: r.athlete_user_id,
          athleteHandle: r.athlete_handle || '',
          resultId: r.result_id ?? null,
          maxSplitNumber: Number(r.max_split_number) || 0,
          runs: [],
        });
      }
      if (r.activity_id) {
        people.get(key).runs.push({
          id: r.activity_id,
          activity_type: r.activity_type,
          started_at: r.started_at,
          distance_meters: Number(r.distance_meters),
          duration_seconds: Number(r.duration_seconds),
          average_pace_seconds_per_km: r.average_pace_seconds_per_km,
        });
      }
    });
    if (onProgress) {
      let runs = 0;
      people.forEach((p) => { runs += p.runs.length; });
      onProgress(runs);
    }

    if (!full) break;
    after = keep[keep.length - 1].registration_id;
    if (highest !== null && Number(after) >= highest) break;
  }
  return [...people.values()];
}

export const DAILY_ATTACH_PAGE_QUERY = 'organizer_daily_attachable_page';

/** Runs per page in the attach modal; the saved query's own LIMIT. */
export const DAILY_ATTACH_PAGE_SIZE = 100;

/**
 * One page of `dayNumber`'s attachable runs, one row per run, with the day's
 * totals. `page` is 1-based.
 *
 * Unlike fetchDailyAttachables this THROWS on a refusal: the modal is only
 * opened for the race's owner, so a failure here (most likely the analytics
 * throttle) is an error to show and retry, not "not your race".
 */
/** A typed search as a LIKE fragment: its own %, _ and \\ match literally. */
export function likeFragment(text) {
  return String(text || '').trim().replace(/[\\%_]/g, (c) => `\\${c}`);
}

export async function fetchDailyAttachPage(api, {
  eventActivityId, dayNumber, minDistanceMeters = 0, page = 1, search = '', signal,
} = {}) {
  const params = new URLSearchParams({
    event_activity_id: eventActivityId,
    min_distance_meters: Number(minDistanceMeters) || 0,
    day_number: dayNumber,
    row_offset: Math.max(0, (Number(page) || 1) - 1) * DAILY_ATTACH_PAGE_SIZE,
    search: likeFragment(search),
  }).toString();
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${DAILY_ATTACH_PAGE_QUERY}/run/?${params}`,
    { method: 'GET', signal },
  );
  if (!resp.ok) {
    const message = resp.status === 429
      ? 'Too many requests just now. Wait a moment and try again.'
      : await readError(resp, 'Could not load the runs for this day.');
    throw new Error(message);
  }
  const rows = rowsAsObjects(await resp.json());
  return {
    totalRuns: rows.length ? Number(rows[0].total_runs) || 0 : 0,
    totalEntrants: rows.length ? Number(rows[0].total_entrants) || 0 : 0,
    // Runs the search found; the whole day when there is no search.
    totalMatches: rows.length ? Number(rows[0].total_matches ?? rows[0].total_runs) || 0 : 0,
    rows: rows.map((r) => ({
      registrationId: r.registration_id,
      athleteUserId: r.athlete_user_id,
      athleteHandle: r.athlete_handle || '',
      resultId: r.result_id ?? null,
      maxSplitNumber: Number(r.max_split_number) || 0,
      run: {
        id: r.activity_id,
        activity_type: r.activity_type,
        started_at: r.started_at,
        distance_meters: Number(r.distance_meters),
        duration_seconds: Number(r.duration_seconds),
        average_pace_seconds_per_km: r.average_pace_seconds_per_km,
      },
    })),
  };
}

async function writeBatch(api, table, rows) {
  const resp = await api.apiCall('/arcee/application/table/save/records/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), operations: [{ table, data: rows }] }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not add these runs.'));
  return resp.json().catch(() => ({}));
}

async function writeOne(api, table, payload) {
  const resp = await api.apiCall('/arcee/application/table/save/record/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), table_name: table, payload }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not add this run.'));
  return resp.json().catch(() => ({}));
}

function isNumberCollision(message) {
  return /uq_event_result_split_number|duplicate key/i.test(String(message || ''));
}

/** registrationId → { resultId, maxSplitNumber }, read fresh for these days. */
async function entriesFor(api, { eventActivityId, days, minDistanceMeters, registrationIds }) {
  const out = new Map();
  for (const dayNumber of days) {
    const people = await fetchDailyAttachables(api, {
      eventActivityId, dayNumber, minDistanceMeters, registrationIds, strict: true,
    });
    people.forEach((p) => {
      if (p.resultId === null || p.resultId === undefined) return;
      const prev = out.get(String(p.registrationId));
      out.set(String(p.registrationId), {
        resultId: p.resultId,
        maxSplitNumber: Math.max(p.maxSplitNumber, prev ? prev.maxSplitNumber : 0),
      });
    });
  }
  return out;
}

/** Entry rows for registrants who have none yet. A duplicate is not an error. */
async function ensureEntries(api, picks, { eventActivityId, timingSourceId }) {
  const missing = new Map();
  picks.forEach((p) => {
    if ((p.resultId === null || p.resultId === undefined) && !missing.has(String(p.registrationId))) {
      missing.set(String(p.registrationId), p);
    }
  });
  const rows = [...missing.values()].map((p) => ({
    ...resultPayloadFor({
      registrationId: p.registrationId, activity: null, timingSourceId, eventActivityId,
      athleteName: p.athleteName,
    }),
    // The entry carries no time of its own; its runs are the splits.
    status: 'IN_PROGRESS',
  }));
  for (let i = 0; i < rows.length; i += ATTACH_BATCH) {
    const slice = rows.slice(i, i + ATTACH_BATCH);
    try {
      await writeBatch(api, RESULT_TABLE, slice);
    } catch (e) {
      // One entrant already entered (another tab, a retry) fails the batch on
      // the registration's unique rule. Write them one by one and let that one
      // fail quietly: the entry it wanted exists.
      for (const one of slice) {
        try { await writeOne(api, RESULT_TABLE, one); } catch (ignored) { /* exists */ }
      }
    }
  }
  return missing.size > 0;
}

/**
 * Attaches every picked run. Picks: { registrationId, resultId, maxSplitNumber,
 * athleteName, run, dayNumber }. Returns the attached activity ids and the
 * refused ones with the server's own sentence.
 */
export async function attachDailyRuns(api, {
  picks, eventActivityId, timingSourceId, minDistanceMeters = 0, onProgress,
} = {}) {
  const all = picks || [];
  const attached = [];
  const refused = [];
  if (!all.length) return { attached, refused };
  // Live counts for the modal's progress panel: how many of the picks have
  // been written, refused, and dealt with so far, out of all of them.
  const tell = () => {
    if (onProgress) {
      onProgress({
        done: attached.length + refused.length, total: all.length,
        attached: attached.length, failed: refused.length,
      });
    }
  };
  tell();

  // 1. Every picked entrant needs an entry row before a split can point at it.
  const created = await ensureEntries(api, all, { eventActivityId, timingSourceId });
  let entries = new Map();
  all.forEach((p) => {
    if (p.resultId !== null && p.resultId !== undefined) {
      entries.set(String(p.registrationId), { resultId: p.resultId, maxSplitNumber: p.maxSplitNumber || 0 });
    }
  });
  if (created) {
    const days = [...new Set(all.map((p) => p.dayNumber))];
    const registrationIds = [...new Set(all.map((p) => p.registrationId))];
    const fresh = await entriesFor(api, { eventActivityId, days, minDistanceMeters, registrationIds });
    entries = new Map([...entries, ...fresh]);
  }

  // 2. Number each run after its entry's highest split so far.
  const next = new Map();
  const jobs = [];
  all.forEach((p) => {
    const entry = entries.get(String(p.registrationId));
    if (!entry) {
      refused.push({ activityId: p.run.id, athleteName: p.athleteName, reason: 'Could not create this entrant\'s entry.' });
      return;
    }
    const n = (next.get(entry.resultId) ?? entry.maxSplitNumber) + 1;
    next.set(entry.resultId, n);
    jobs.push({ pick: p, payload: splitPayloadFor({ resultId: entry.resultId, splitNumber: n, dayNumber: p.dayNumber, run: p.run }) });
  });

  // 3. Write in batches; a refused batch is retried run by run so one bad run
  //    does not take its neighbours down with it.
  for (let i = 0; i < jobs.length; i += ATTACH_BATCH) {
    const slice = jobs.slice(i, i + ATTACH_BATCH);
    try {
      await writeBatch(api, SPLIT_TABLE, slice.map((j) => j.payload));
      slice.forEach((j) => attached.push(j.pick.run.id));
    } catch (e) {
      for (const job of slice) {
        try {
          await writeOne(api, SPLIT_TABLE, job.payload);
          attached.push(job.pick.run.id);
          tell();
        } catch (err) {
          let reason = (err && err.message) || 'Could not attach that run.';
          if (isNumberCollision(reason)) {
            // Somebody attached to this entry meanwhile: renumber once.
            const fresh = await entriesFor(api, {
              eventActivityId, days: [job.pick.dayNumber], minDistanceMeters,
              registrationIds: [job.pick.registrationId],
            });
            const entry = fresh.get(String(job.pick.registrationId));
            if (entry) {
              const taken = Math.max(entry.maxSplitNumber, next.get(entry.resultId) || 0);
              next.set(entry.resultId, taken + 1);
              try {
                await writeOne(api, SPLIT_TABLE, { ...job.payload, split_number: taken + 1 });
                attached.push(job.pick.run.id);
                tell();
                continue;
              } catch (again) {
                reason = (again && again.message) || reason;
              }
            }
          }
          refused.push({ activityId: job.pick.run.id, athleteName: job.pick.athleteName, reason });
          tell();
        }
      }
    }
    tell();
  }
  return { attached, refused };
}

/** Takes one attached run off the board. */
export async function removeDailyAttach(api, { splitId } = {}) {
  if (splitId === null || splitId === undefined) return;
  const resp = await api.apiCall('/arcee/application/table/delete-record/', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), table_name: SPLIT_TABLE, id: splitId }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not remove that run.'));
}
